import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';
import styled from 'styled-components';
import { useCompanyStore } from '@/store/useCompanyStore';
import { semanticColors, colorTokens } from '@/common/styles/colorTokens';
import { MdKeyboardArrowDown, MdKeyboardArrowRight } from 'react-icons/md';
import config from '@/config';
import { useAuthStore } from '@/store/useAuthStore';
import Avatar from '@/common/components/avatar/Avatar';
import { LoginCompanyUserInfo } from '@/common/types/login';

import { SpaceNavigationProps } from '../config/types';
import EmailVerificationModal from '@/common/layout/components/EmailVerificationModal';

// 로컬 그림자 정의
const shadows = {
  md: '0 4px 12px rgba(0, 0, 0, 0.08)',
  lg: '0 12px 24px rgba(0, 0, 0, 0.12)',
};

// 서비스 타입을 한글로 변환하는 함수
const getServiceName = (service: string): string => {
  switch(service) {
    case 'INVESTOR':
      return '투자자 공간';
    case 'STARTUP':
      return '스타트업 공간';
    case 'INNOVATE':
      return '액셀러레이터 공간';
    default:
      return '마이 페이지';
  }
};

// 서비스 타입별 경로를 생성하는 함수
const getServicePath = (service: string, code: string): string => {
  switch(service) {
    case 'INVESTOR':
      return `${config.investorClientUrl}/${code}/home`;
    case 'STARTUP':
      return `${config.startupClientUrl}/${code}/company-info`;
    case 'INNOVATE':
      return `${config.innovateClientUrl}/${code}/longlist`;
    default:
      return service.toLowerCase();
  }
};

// 스타일 컴포넌트 정의
const HeaderContainer = styled.div`
  height: fit-content;
  display: flex;
  align-items: center;
  padding: 0.75rem 1.25rem;
  gap: 0.875rem;
  cursor: pointer;
  position: relative;
  border-radius: 12px;
  transition: all 0.2s ease;
  margin: 0.5rem 0.75rem;

  &:hover {
    background-color: ${colorTokens.gray[100]};
  }
`;

const CompanyInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CompanyName = styled.div`
  font-weight: 600;
  color: ${colorTokens.gray[900]};
  font-size: 0.9375rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SpaceType = styled.div`
  font-size: 0.75rem;
  color: ${colorTokens.gray[500]};
  margin-top: 2px;
  font-weight: 500;
`;

const ArrowIcon = styled.div<{ $isOpen: boolean }>`
  display: flex;
  align-items: center;
  color: ${colorTokens.gray[400]};
  transition: transform 0.2s ease;
  transform: ${props => props.$isOpen ? 'rotate(180deg)' : 'rotate(0)'};
`;

// Portal 드롭다운 스타일 (접힌 상태)
const PortalDropdown = styled.div`
  position: fixed;
  min-width: 240px;
  background: white;
  border: 1px solid ${colorTokens.gray[200]};
  border-radius: 12px;
  box-shadow: ${shadows.lg};
  z-index: 9999;
  overflow: visible;
  padding: 0.5rem;
  animation: fadeIn 0.2s ease;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateX(-8px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

// Portal 드롭다운 스타일 (펼친 상태)
const PortalDropdownExpanded = styled.div`
  position: fixed;
  width: 250px;
  background-color: white;
  border: 1px solid ${colorTokens.gray[200]};
  border-radius: 12px;
  box-shadow: ${shadows.lg};
  z-index: 9999;
  overflow: visible;
  padding: 0.5rem;
  animation: fadeInDown 0.2s ease;
  
  @keyframes fadeInDown {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;

const CompanyListItem = styled.div<{ $isDisabled?: boolean }>`
  display: flex;
  align-items: center;
  padding: 0.75rem 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  border-radius: 8px;
  gap: 0.75rem;

  &:hover {
    background-color: ${colorTokens.gray[50]};
  }
`;

const CompanyListItemLogo = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const CompanyListItemName = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const CompanyItemName = styled.div`
  font-weight: 600;
  font-size: 0.875rem;
  color: ${colorTokens.gray[800]};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const ServiceInfo = styled.div`
  font-size: 0.75rem;
  line-height: 1.2;
  font-weight: 500;
`;

const SpaceListContainer = styled.div<{ isVisible: boolean }>`
  position: absolute;
  left: calc(100% + 4px);
  top: -4px;
  background-color: white;
  border-radius: 10px;
  border: 1px solid ${colorTokens.gray[200]};
  box-shadow: ${shadows.md};
  min-width: 200px;
  padding: 0.4rem;
  opacity: ${props => props.isVisible ? 1 : 0};
  visibility: ${props => props.isVisible ? 'visible' : 'hidden'};
  transform: ${props => props.isVisible ? 'translateX(0)' : 'translateX(-4px)'};
  transition: all 0.15s ease;
  z-index: 1001;
`;

const SpaceListItem = styled.div<{ $isDisabled?: boolean }>`
  padding: 0.625rem 0.75rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.8125rem;
  border-radius: 6px;
  
  &:hover {
    background-color: ${colorTokens.gray[50]};
  }
`;

const CurrentSpaceBadge = styled.span`
  display: inline-flex;
  align-items: center;
  background-color: ${colorTokens.brand[100]};
  color: ${colorTokens.brand[600]};
  font-size: 0.6875rem;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 6px;
  margin-left: auto;
`;

const ActiveSpaceIndicator = styled.div`
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background-color: ${colorTokens.brand[500]};
  margin-right: 8px;
  flex-shrink: 0;
`;

const LogoutButton = styled.div`
  padding: 0.75rem 0.875rem;
  cursor: pointer;
  transition: all 0.2s ease;
  font-size: 0.8125rem;
  font-weight: 600;
  color: ${colorTokens.gray[500]};
  border-top: 1px solid ${colorTokens.gray[100]};
  margin-top: 0.5rem;
  border-radius: 0 0 8px 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  
  &:hover {
    background-color: ${colorTokens.gray[50]};
    color: ${colorTokens.red[500]};
  }
`;

const SpaceItemContent = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
  gap: 4px;
`;

const CollapsedDropdownItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0.875rem;
  cursor: pointer;
  transition: all 0.15s ease;
  border-radius: 8px;
  
  &:hover {
    background: ${colorTokens.gray[50]};
  }
`;

// 여러 서비스가 있는 회사 아이템 (서브메뉴 지원)
const CollapsedCompanyItem = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 0.875rem;
  cursor: pointer;
  transition: all 0.15s ease;
  border-radius: 8px;
  
  &:hover {
    background: ${colorTokens.gray[50]};
  }
`;

// 접힌 상태 서브메뉴 (서비스 목록)
const CollapsedSpaceSubMenu = styled.div<{ $isVisible: boolean }>`
  position: absolute;
  left: calc(100% + 8px);
  top: 0;
  min-width: 190px;
  background: white;
  border: 1px solid ${colorTokens.gray[200]};
  border-radius: 10px;
  box-shadow: ${shadows.md};
  padding: 0.4rem;
  opacity: ${props => props.$isVisible ? 1 : 0};
  visibility: ${props => props.$isVisible ? 'visible' : 'hidden'};
  transform: ${props => props.$isVisible ? 'translateX(0)' : 'translateX(-4px)'};
  transition: all 0.15s ease;
  z-index: 10000;
`;

const CollapsedSpaceSubMenuItem = styled.div`
  padding: 0.625rem 0.75rem;
  cursor: pointer;
  font-size: 0.8125rem;
  border-radius: 6px;
  transition: all 0.15s ease;
  
  &:hover {
    background: ${colorTokens.gray[50]};
  }
  
  span {
    color: ${colorTokens.gray[700]};
    font-weight: 500;
  }
`;

const CollapsedAvatar = styled.div`
  width: 28px;
  height: 28px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  
  & > div {
    width: 100% !important;
    height: 100% !important;
  }
  
  img {
    width: 100% !important;
    height: 100% !important;
    object-fit: cover;
  }
`;

const CollapsedTooltip = styled.span`
  position: absolute;
  left: 100%;
  top: 50%;
  transform: translateY(-50%);
  margin-left: 8px;
  padding: 0.5rem 0.75rem;
  background: ${colorTokens.gray[800]};
  color: white;
  font-size: 0.8125rem;
  font-weight: 500;
  border-radius: 6px;
  white-space: nowrap;
  opacity: 0;
  visibility: hidden;
  transition: all 0.15s ease;
  z-index: 2000;
  pointer-events: none;
  line-height: 1.4;
  
  &::before {
    content: '';
    position: absolute;
    right: 100%;
    top: 50%;
    transform: translateY(-50%);
    border: 5px solid transparent;
    border-right-color: ${colorTokens.gray[800]};
  }
`;

const CollapsedUserContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  margin: 0.5rem auto;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.15s ease;
  
  &:hover {
    background: ${colorTokens.gray[100]};
    
    ${CollapsedTooltip} {
      opacity: 1;
      visibility: visible;
    }
  }
`;

const CollapsedDropdownHeader = styled.div`
  padding: 0.75rem 1rem;
  font-size: 0.75rem;
  font-weight: 600;
  color: ${colorTokens.gray[500]};
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border-bottom: 1px solid ${colorTokens.gray[100]};
`;

const CollapsedDropdownItemText = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.125rem;
  min-width: 0;
  
  span {
    font-size: 0.8125rem;
    font-weight: 500;
    color: ${colorTokens.gray[800]};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
  
  small {
    font-size: 0.6875rem;
    color: ${colorTokens.gray[500]};
  }
`;

const SpaceNavigation: React.FC<SpaceNavigationProps> = ({ isCollapsed = false }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hoveredCompanyIndex, setHoveredCompanyIndex] = useState<number | null>(null);
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(false);
  const { activeCompany, companies } = useCompanyStore();
  const { user } = useAuthStore();
  const [selectedSpace, setSelectedSpace] = useState<LoginCompanyUserInfo | null>(null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });

  const triggerRef = useRef<HTMLDivElement>(null);
  const portalDropdownRef = useRef<HTMLDivElement>(null);

  // 드롭다운 위치 계산
  useEffect(() => {
    if (isDropdownOpen && triggerRef.current) {
      const rect = triggerRef.current.getBoundingClientRect();
      if (isCollapsed) {
        setDropdownPosition({
          top: rect.top,
          left: rect.right + 8,
        });
      } else {
        setDropdownPosition({
          top: rect.bottom + 8,
          left: rect.left,
        });
      }
    }
  }, [isDropdownOpen, isCollapsed]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const isOutsideTrigger = triggerRef.current && !triggerRef.current.contains(target);
      const isOutsidePortal = portalDropdownRef.current && !portalDropdownRef.current.contains(target);
      
      if (isOutsideTrigger && isOutsidePortal) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleCompanySpaceSelect = (companyUser: any, service: string) => {
    const currentService = getCurrentService();
    if(currentService === service && companyUser?.company?.code === activeCompany?.code) return;

    if(service !== 'MYSPACE') {
      if(!companyUser?.emailVerified) {
        setIsDropdownOpen(false);
        setIsEmailModalOpen(true);
        return;
      }
    }

    setIsDropdownOpen(false);
    if(service === 'MYSPACE') {
      window.location.href = `${config.mySpaceClientUrl}/my-space`;
    } else {
      const path = getServicePath(service, companyUser.company.code);
      window.location.href = path;
    }
  };

  const getCurrentService = (): string | null => {
    const domain = window.location.origin;

    if (domain.includes(config.investorClientUrl)) return 'INVESTOR';
    if (domain.includes(config.startupClientUrl)) return 'STARTUP';
    if (domain.includes(config.innovateClientUrl)) return 'INNOVATE';
    if (domain.includes(config.mySpaceClientUrl)) return 'MYSPACE';
    return null;
  };

  const currentService = getCurrentService();

  const handleLogout = () => {
    setIsDropdownOpen(false);
    window.location.replace(`${config.mySpaceClientUrl}/logout`);
  };

  // 접힌 상태 렌더링
  if (isCollapsed) {
    return (
      <div>
        <CollapsedUserContainer ref={triggerRef} onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
          <CollapsedAvatar>
            <Avatar sizes="md" src={user?.profileImage} alt={user?.name || ''} isUser={true}/>
          </CollapsedAvatar>
          <CollapsedTooltip>
            {user?.name || '사용자'}
            <br />
            <span style={{ fontSize: '11px', opacity: 0.8 }}>
              {currentService ? getServiceName(currentService) : ''}
            </span>
          </CollapsedTooltip>
        </CollapsedUserContainer>
        
        {/* 접힌 상태 드롭다운 - Portal로 렌더링 */}
        {isDropdownOpen && ReactDOM.createPortal(
          <PortalDropdown 
            ref={portalDropdownRef}
            style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
          >
            <CollapsedDropdownHeader>스페이스 전환</CollapsedDropdownHeader>
            <CollapsedDropdownItem onClick={(e) => {
              e.stopPropagation();
              handleCompanySpaceSelect(undefined, 'MYSPACE');
            }}>
              <Avatar sizes="md" src={user?.profileImage} alt={user?.name || ''} isUser={true}/>
              <CollapsedDropdownItemText>
                <span>{user?.name}님의</span>
                <small>마이 페이지</small>
              </CollapsedDropdownItemText>
            </CollapsedDropdownItem>
            
            {companies.map((companyUser, index) => (
              <CollapsedCompanyItem 
                key={companyUser.company.id}
                onMouseEnter={() => setHoveredCompanyIndex(index)}
                onMouseLeave={() => setHoveredCompanyIndex(null)}
                onClick={(e) => {
                  e.stopPropagation();
                  if (companyUser.emailVerified && companyUser.company.service.length > 1) return;
                  
                  if (!companyUser.emailVerified) {
                    setIsEmailModalOpen(true);
                    setSelectedSpace(companyUser);
                    return;
                  }
                  handleCompanySpaceSelect(companyUser, companyUser.company.service[0]);
                }}
              >
                <Avatar sizes="md" src={companyUser.company.logo} alt={companyUser.company.name} isUser={false}/>
                <CollapsedDropdownItemText>
                  <span>{companyUser.company.name}</span>
                  {companyUser.company.service.length === 1 && (
                    <small style={{ color: !companyUser.emailVerified ? semanticColors.text.onDanger.secondary : 'inherit' }}>
                      {!companyUser.emailVerified ? '이메일 인증 필요' : getServiceName(companyUser.company.service[0])}
                    </small>
                  )}
                </CollapsedDropdownItemText>
                {companyUser.emailVerified && companyUser.company.service.length > 1 && (
                  <MdKeyboardArrowRight size={16} color={colorTokens.gray[400]} />
                )}
                {activeCompany?.id === companyUser.company.id && companyUser.company.service.length === 1 && (
                  <CurrentSpaceBadge>현재</CurrentSpaceBadge>
                )}
                
                {companyUser.emailVerified && companyUser.company.service.length > 1 && (
                  <CollapsedSpaceSubMenu $isVisible={hoveredCompanyIndex === index}>
                    {companyUser.company.service.map((service) => (
                      <CollapsedSpaceSubMenuItem 
                        key={service}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCompanySpaceSelect(companyUser, service);
                        }}
                      >
                        <SpaceItemContent>
                          {activeCompany?.id === companyUser.company.id && currentService === service && (
                            <ActiveSpaceIndicator />
                          )}
                          <span>{getServiceName(service)}</span>
                          {activeCompany?.id === companyUser.company.id && currentService === service && (
                            <CurrentSpaceBadge>현재</CurrentSpaceBadge>
                          )}
                        </SpaceItemContent>
                      </CollapsedSpaceSubMenuItem>
                    ))}
                  </CollapsedSpaceSubMenu>
                )}
              </CollapsedCompanyItem>
            ))}
            
            <LogoutButton onClick={(e) => {
              e.stopPropagation();
              handleLogout();
            }}>
              로그아웃
            </LogoutButton>
          </PortalDropdown>,
          document.body
        )}

        {selectedSpace && isEmailModalOpen && (    
          <EmailVerificationModal
            isOpen={isEmailModalOpen}
            onClose={() => {
              setIsEmailModalOpen(false);
              setSelectedSpace(null);
            }}
            user={selectedSpace}
          />
        )}
      </div>
    );
  }

  return (
    <div style={{width: '100%'}}>
      <HeaderContainer ref={triggerRef} onClick={() => setIsDropdownOpen(!isDropdownOpen)}>
        <Avatar sizes="md" src={user?.profileImage} alt={user?.name || ''} isUser={true}/>
        <CompanyInfo>
          <CompanyName>
            {user?.name || '로그인이 필요합니다.'}
            <ArrowIcon $isOpen={isDropdownOpen}>
              <MdKeyboardArrowDown size={20} />
            </ArrowIcon>
          </CompanyName>
          <SpaceType>
            {currentService ? getServiceName(currentService) : '서비스를 선택하세요'}
          </SpaceType>
        </CompanyInfo>
      </HeaderContainer>

      {isDropdownOpen && ReactDOM.createPortal(
        <PortalDropdownExpanded 
          ref={portalDropdownRef}
          style={{ top: dropdownPosition.top, left: dropdownPosition.left }}
        >
          <CompanyListItem onClick={() => {
            handleCompanySpaceSelect(undefined, 'MYSPACE');
          }}>
            <CompanyListItemLogo>
              <Avatar sizes="md" src={user?.profileImage} alt={user?.name || '유저 이름'} isUser={true}/>
            </CompanyListItemLogo>
            <CompanyListItemName>
              <CompanyItemName>{user?.name}님의</CompanyItemName>
              <ServiceInfo style={{color: colorTokens.gray[500]}}>마이 페이지</ServiceInfo>
            </CompanyListItemName>
          </CompanyListItem>
          {
            companies.map((companyUser, index) => {
              return (
                <CompanyListItem 
                  key={companyUser.company.id}
                  onMouseEnter={() => setHoveredCompanyIndex(index)}
                  onMouseLeave={() => setHoveredCompanyIndex(null)}
                  onClick={() => {
                    if(companyUser.emailVerified && companyUser.company.service.length > 1) return;
                    
                    if(!companyUser.emailVerified) {
                      setIsEmailModalOpen(true);
                      setSelectedSpace(companyUser);
                      return;
                    }
                    
                    handleCompanySpaceSelect(companyUser, companyUser.company.service[0]);
                  }} 
                >
                <CompanyListItemLogo>
                  <Avatar sizes="md" src={companyUser.company.logo} alt={companyUser.company.name} isUser={false}/>
                </CompanyListItemLogo>
                <CompanyListItemName>
                  <CompanyItemName>{companyUser.company.name}</CompanyItemName>
                  {companyUser.company.service.length === 1 && (
                    <ServiceInfo style={{color: !companyUser.emailVerified ? semanticColors.text.onDanger.secondary : colorTokens.gray[500]}}>
                      {!companyUser.emailVerified ? '이메일 인증 필요' : getServiceName(companyUser.company.service[0])}
                    </ServiceInfo>
                  )}
                </CompanyListItemName>
                {companyUser.emailVerified && companyUser.company.service.length > 1 && (
                  <MdKeyboardArrowRight size={20} color={colorTokens.gray[400]} />
                )}

                {companyUser.emailVerified && companyUser.company.service.length > 1 && (
                  <SpaceListContainer isVisible={hoveredCompanyIndex === index}>
                    {companyUser.company.service.map((service) => {
                      const isDisabled = !companyUser.emailVerified;
                      return (
                        <SpaceListItem 
                          key={service}
                          $isDisabled={isDisabled}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (isDisabled) {
                              setSelectedSpace(companyUser);
                              setIsEmailModalOpen(true);
                              return;
                            }
                            handleCompanySpaceSelect(companyUser, service);
                          }}
                        >
                          <SpaceItemContent>
                            {companyUser.emailVerified && activeCompany?.id === companyUser.company.id && currentService === service && (
                              <ActiveSpaceIndicator />
                            )}
                            <span style={{color: !companyUser.emailVerified ? semanticColors.text.onDanger.secondary : 'inherit'}}>
                              {!companyUser.emailVerified ? '이메일 인증 필요' : getServiceName(service)}
                            </span>
                            {companyUser.emailVerified && activeCompany?.id === companyUser.company.id && currentService === service && (
                              <CurrentSpaceBadge>현재</CurrentSpaceBadge>
                            )}
                          </SpaceItemContent>
                        </SpaceListItem>
                      );
                    })}
                  </SpaceListContainer>
                )}
              </CompanyListItem>
              );
            })
          }
          <LogoutButton onClick={handleLogout}>
            로그아웃
          </LogoutButton>
        </PortalDropdownExpanded>,
        document.body
      )}

      {selectedSpace && isEmailModalOpen && (    
        <EmailVerificationModal
          isOpen={isEmailModalOpen}
          onClose={() => {
            setIsEmailModalOpen(false);
            setSelectedSpace(null);
          }}
          user={selectedSpace}
        />
      )}
    </div>
  );
};

export default SpaceNavigation;